import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, Circle, ListChecks, Loader2, PauseCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";
import { detailInTaal, stapInTaal } from "@/lib/codestappen-taal";

/**
 * CA7 (besluit Frank 1 november 2026): een hele app in deelstappen.
 *
 * Eerst het plan als takenlijst; elke deelstap is een eigen rit met toetsen, en de volgende start pas op jouw Bevestig,
 * nadat je de bestanden van de vorige hebt toegepast of geweigerd. Hooguit 60 minuten per app-opdracht. Een deelstap die
 * niet af kwam, staat op gestopt en start bij Bevestig opnieuw (hervatten).
 */
export function minuten(seconden) {
  return Math.max(0, Math.round((seconden || 0) / 60));
}

const ICOON = { klaar: CheckCircle2, gestopt: PauseCircle, bezig: Loader2, open: Circle };

export default function DeelstappenPaneel({ workspaceId, onRit, verder = 0 }) {
  const { t, taal } = useTaal();
  const [plan, setPlan] = useState(null);
  const [opdracht, setOpdracht] = useState("");
  const [bezig, setBezig] = useState("");
  const [weg, setWeg] = useState(false);

  const lees = useCallback(() => {
    axios.get(`${API}/workspaces/${workspaceId}/deelstappen`).then((r) => setPlan(r.data.plan)).catch(() => {});
  }, [workspaceId]);
  useEffect(() => { lees(); }, [lees]);

  const fout = (e, standaard) => {
    const d = e?.response?.data?.detail;
    toast.error(typeof d === "string" ? t(d) : standaard);
  };

  async function maakPlan(e) {
    e.preventDefault();
    setBezig("plan");
    try {
      setPlan((await axios.post(`${API}/workspaces/${workspaceId}/deelstappen`, { opdracht: opdracht.trim() })).data.plan);
      setOpdracht("");
    } catch (err) {
      fout(err, t("Het plan kon niet worden gemaakt."));
    } finally {
      setBezig("");
    }
  }

  async function volgende() {
    setBezig("stap");
    try {
      const { data } = await axios.post(`${API}/workspaces/${workspaceId}/deelstappen/volgende`);
      setPlan(data.plan);
      onRit?.(data);
    } catch (err) {
      fout(err, t("De deelstap kon niet starten."));
      lees();
    } finally {
      setBezig("");
    }
  }

  // Glasronde 0.9.229 fout 6: na Installeer in Code gaat de deelstap die erom vroeg vanzelf verder.
  useEffect(() => {
    if (verder) volgende();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verder]);

  async function gooiWeg() {
    if (!weg) return setWeg(true);
    setWeg(false);
    try {
      await axios.delete(`${API}/workspaces/${workspaceId}/deelstappen`);
      setPlan(null);
    } catch (err) {
      fout(err, t("Dat lukte niet."));
    }
  }

  const af = plan && plan.stappen.every((s) => s.status === "klaar");
  const volgend = plan && plan.stappen.find((s) => s.status !== "klaar");
  return (
    <div className="space-y-2 border-t pt-2" data-testid="deelstappen-paneel">
      <span className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
        <ListChecks className="h-3.5 w-3.5" />{t("Of laat de agent een hele app bouwen in deelstappen")}
      </span>
      {!plan || af ? (
        <form onSubmit={maakPlan} className="flex gap-2" data-testid="deelstappen-vorm">
          <Input value={opdracht} onChange={(e) => setOpdracht(e.target.value)} aria-label={t("Wat moet de app doen?")}
                 placeholder={t("Bijvoorbeeld: een app die mijn uren per week telt, met toetsen")} data-testid="deelstappen-opdracht" />
          <Button type="submit" variant="outline" className="tap-target shrink-0" disabled={Boolean(bezig) || !opdracht.trim()}
                  data-testid="deelstappen-plan">
            {bezig === "plan" && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}{t("Maak het plan")}
          </Button>
        </form>
      ) : null}
      {plan && (
        <div className="space-y-1 rounded-md border p-2" data-testid="deelstappen-plan-lijst">
          <p className="break-words text-xs font-medium">{plan.opdracht}</p>
          <ol className="space-y-1">
            {plan.stappen.map((s) => {
              const Icoon = ICOON[s.status] || Circle;
              return (
                <li key={s.nr} className="flex flex-wrap items-start gap-2 text-xs" data-testid={`deelstap-${s.nr}`} data-status={s.status}>
                  <Icoon className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${s.status === "klaar" ? "text-emerald-600" : s.status === "gestopt" ? "text-amber-600" : "text-muted-foreground"}`} />
                  <span className="min-w-0 flex-1 break-words">{s.nr}. {s.tekst}</span>
                  <span className="shrink-0 text-muted-foreground">{t(s.status)}</span>
                  {/* Glasronde 0.9.229 fout 4: waarom hij stopte, in gewone taal. */}
                  {s.status === "gestopt" && s.reden && (
                    <span className="basis-full break-words pl-5 text-amber-700" data-testid={`deelstap-reden-${s.nr}`}>
                      {stapInTaal(s.reden.titel, taal)}{s.reden.detail ? `: ${detailInTaal(s.reden.detail, taal)}` : ""}
                    </span>
                  )}
                </li>
              );
            })}
          </ol>
          <p className="text-xs text-muted-foreground" data-testid="deelstappen-tijd">
            {t("{gebruikt} van {budget} minuten gebruikt", { gebruikt: minuten(plan.verbruikt_s), budget: minuten(plan.budget_s) })}
          </p>
          {af ? (
            <p className="text-xs text-emerald-700" data-testid="deelstappen-af">{t("Alle deelstappen zijn klaar.")}</p>
          ) : (
            <>
              <p className="text-xs text-muted-foreground">
                {t("Pas na elke deelstap de bestanden toe of weiger ze; daarna start Bevestig de volgende.")}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" className="tap-target" onClick={volgende} disabled={Boolean(bezig)} data-testid="deelstap-volgende">
                  {bezig === "stap" && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                  {volgend?.status === "gestopt"
                    ? t("Bevestig en hervat deelstap {nr}", { nr: volgend.nr })
                    : t("Bevestig en start deelstap {nr}", { nr: volgend?.nr })}
                </Button>
                <Button size="sm" variant={weg ? "destructive" : "ghost"} className="tap-target" onClick={gooiWeg}
                        disabled={Boolean(bezig)} data-testid="deelstappen-weg">
                  <Trash2 className="h-4 w-4 mr-1" />{weg ? t("Zeker weten?") : t("Gooi het plan weg")}
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
